import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useNavigate } from "react-router-dom";
import StatusTag from "../../components/StatusTag";
import { useContext, useState } from "react";
import { useCentersFromRequirements } from "../../hooks/queries";
import { Dropdown } from "primereact/dropdown";
import { ToastContext } from "../../ToastContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { changeCaseMedicalCenter } from "../../services/medicalCases";

/**
 * Dialog to display assigned medical centers for a sub administrator case
 * @param {object} props
 * @param {boolean} props.visible - Determines if the dialog is visible
 * @param {function} props.onHide - Function to call when the dialog is closed
 * @param {number} props.caseId - The ID of the case
 * @param {import("../../services/medicalCases").ChildMedicalCase[]?} props.caseChildren - The children of the case
 * @param {number} props.subAdministratorId - The ID of the sub administrator
 */
export default function AssignedCentersDialog({
  visible,
  onHide,
  caseId,
  caseChildren,
  subAdministratorId,
}) {
  const navigate = useNavigate();

  const [childForProviderChange, setChildForProviderChange] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);

  const availableCenters = useCentersFromRequirements({
    requirements: childForProviderChange?.requirements.map(
      (r) => r.requirement
    ),
    subAdministrator: subAdministratorId,
    enabled: !!childForProviderChange,
  });

  const toast = useContext(ToastContext);

  const queryClient = useQueryClient();

  const changeMedicalCenterMutation = useMutation({
    mutationFn: changeCaseMedicalCenter,
    onSuccess: ({ type, message }) => {
      if (type === "success") {
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: message,
        });
        setChildForProviderChange(null);
        setSelectedCenter(null);
        queryClient.invalidateQueries({
          queryKey: ["medicalCase", caseId],
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: message,
        });
      }
    },
  });

  const hideDialog = () => {
    if (changeMedicalCenterMutation.isPending) return;
    setSelectedCenter(null);
    setChildForProviderChange(null);
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={hideDialog}
      draggable={false}
      className="md:w-4"
      header={
        childForProviderChange ? "Cambiar proveedor" : "Proveedores asignados"
      }
    >
      {!childForProviderChange && (
        <div className="flex flex-column gap-3">
          {caseChildren?.map((child) => (
            <div
              className="border-1 border-solid border-round-lg border-300"
              key={child.id}
            >
              <Button
                className="w-full border-none pt-3 px-2 cursor-pointer block text-left bg-transparent text-color"
                tooltip="Ver detalle"
                tooltipOptions={{ position: "top" }}
                onClick={() => navigate(`/casos/reasignados/${child.id}`)}
              >
                <h4 className="mt-0 mb-1">
                  <i className="pi pi-building-columns"></i>{" "}
                  {child.medical_center_name} <StatusTag value={child.status} />
                </h4>
                <ul>
                  {child.requirements.map((req) => (
                    <li key={req.id}>{req.requirement_name}</li>
                  ))}
                </ul>
              </Button>
              <Button
                label="Cambiar proveedor"
                className="w-full border-round-sm"
                onClick={() => setChildForProviderChange(child)}
              />
            </div>
          ))}
        </div>
      )}
      {childForProviderChange && (
        <div>
          <p>Seleccione el nuevo proveedor para el caso</p>
          <p>
            Este proveedor reemplazará a{" "}
            <b>{childForProviderChange.medical_center_name}</b>
          </p>
          <Dropdown
            value={selectedCenter}
            onChange={(e) => setSelectedCenter(e.value)}
            options={
              availableCenters.data?.filter(
                (center) =>
                  center.medical_center_name !==
                  childForProviderChange.medical_center_name
              ) || []
            }
            loading={availableCenters.isLoading}
            disabled={availableCenters.isLoading}
            optionLabel="medical_center_name"
            optionValue="medical_center"
            placeholder="Seleccione un proveedor"
            className="w-full"
            filter
          />
          <div className="flex mt-3 justify-content-between gap-3">
            <Button
              className="w-full"
              severity="secondary"
              label="Cancelar"
              onClick={() => setChildForProviderChange(null)}
              disabled={changeMedicalCenterMutation.isPending}
            />
            <Button
              className="w-full"
              label="Confirmar"
              disabled={
                selectedCenter === null || changeMedicalCenterMutation.isPending
              }
              loading={changeMedicalCenterMutation.isPending}
              onClick={() => {
                changeMedicalCenterMutation.mutate({
                  caseId: childForProviderChange.id,
                  medicalCenterId: selectedCenter,
                });
              }}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
}
